"use client";
import { useCallback, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { useRooms } from "@/components/calculators/room-context";
import { toNum, fmt } from "@/components/calculators/calc-constants";
import { NumField } from "@/components/calculators/calc-ui";

/* ── Treatment recommendation types ── */
type OccupancyStatus = "occupied" | "vacatable";
type SmokeLevel = "heavy" | "moderate";

/* ── Contents inventory ── */
type ItemStatus = "total-loss" | "salvageable" | "cleanable";

interface ContentItem {
  id: string;
  description: string;
  quantity: number;
  replacementValue: number;
  status: ItemStatus;
}

function newItem(): ContentItem {
  return {
    id: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
    description: "",
    quantity: 1,
    replacementValue: 0,
    status: "salvageable",
  };
}

const STATUS_LABEL: Record<ItemStatus, string> = {
  "total-loss": "Total Loss",
  "salvageable": "Salvageable",
  "cleanable": "Cleanable",
};

const STATUS_COLOR: Record<ItemStatus, string> = {
  "total-loss": "#f87171",
  "salvageable": "#4ade80",
  "cleanable": "#D4A84B",
};

export function FireSmokeTab() {
  const { summary } = useRooms();

  /* Section 1 — Ozone vs Hydroxyl */
  const [occupancy, setOccupancy] = useState<OccupancyStatus>("occupied");
  const [unitCoverage, setUnitCoverage] = useState("600"); /* ft³ per unit for hydroxyl default */

  /* Section 2 — Air Scrubbers */
  const [smokeLevel, setSmokeLevel] = useState<SmokeLevel>("moderate");
  const [scrubberCfm, setScrubberCfm] = useState("500");
  const [targetAchScrub, setTargetAchScrub] = useState("4");

  /* Section 3 — Contents */
  const [items, setItems] = useState<ContentItem[]>([newItem()]);

  /* Structure volume. It used to come from the Job Setup room list and
   * nowhere else, so with no rooms this tab sized every ozone, hydroxyl and
   * scrubber recommendation off zero. The tab section was hidden until a room
   * existed, which hid the problem rather than fixing it; now that the gate is
   * gone, manual dimensions are the fallback — the same override the Mold tab
   * has always had. */
  const [manualL, setManualL] = useState("");
  const [manualW, setManualW] = useState("");
  const [manualH, setManualH] = useState("");

  const manualVolume = useMemo(() => {
    const L = toNum(manualL), W = toNum(manualW), H = toNum(manualH);
    return L > 0 && W > 0 && H > 0 ? L * W * H : 0;
  }, [manualL, manualW, manualH]);

  const volume = manualVolume || summary.totalVolumeFt3;

  /* Ozone/Hydroxyl sizing */
  const treatmentRec = useMemo(() => {
    const useHydroxyl = occupancy === "occupied";
    const coverageMin = useHydroxyl ? 400 : 500;
    const coverageMax = useHydroxyl ? 600 : 800;
    const cfmPerUnit = toNum(unitCoverage) || (useHydroxyl ? 500 : 650);
    const unitCountLow = volume > 0 ? Math.ceil(volume / coverageMax) : 0;
    const unitCountHigh = volume > 0 ? Math.ceil(volume / coverageMin) : 0;
    /* Air-turnover time, in MINUTES.
     * volume (ft³) / CFM (ft³/min) = minutes per single air change.
     * Multiplied by 1.5× for a treatment-cycle safety factor.
     * Previously this value was labeled "hours" in the UI — fixed. */
    const midCount = Math.ceil((unitCountLow + unitCountHigh) / 2) || 1;
    const cycleMinutes = volume > 0 && midCount > 0 ? (volume / (midCount * cfmPerUnit)) * 1.5 : 0;
    return { useHydroxyl, unitCountLow, unitCountHigh, cycleMinutes, coverageMin, coverageMax };
  }, [occupancy, volume, unitCoverage]);

  /* Air scrubber sizing */
  const scrubbers = useMemo(() => {
    const ach = toNum(targetAchScrub) || 4;
    const cfm = toNum(scrubberCfm) || 500;
    const reqCfm = (volume * ach) / 60;
    const count = reqCfm > 0 && cfm > 0 ? Math.ceil(reqCfm / cfm) : 0;
    const filterIntervalHours = smokeLevel === "heavy" ? "12–24" : "48";
    return { count, reqCfm, ach, filterIntervalHours };
  }, [volume, targetAchScrub, scrubberCfm, smokeLevel]);

  /* Contents totals */
  const contentsTotals = useMemo(() => {
    let totalLossValue = 0, salvageableValue = 0, cleanableValue = 0;
    let totalLossCount = 0, salvageableCount = 0, cleanableCount = 0;
    for (const item of items) {
      const val = item.quantity * item.replacementValue;
      if (item.status === "total-loss") { totalLossValue += val; totalLossCount += item.quantity; }
      else if (item.status === "salvageable") { salvageableValue += val; salvageableCount += item.quantity; }
      else { cleanableValue += val; cleanableCount += item.quantity; }
    }
    return { totalLossValue, salvageableValue, cleanableValue, totalLossCount, salvageableCount, cleanableCount };
  }, [items]);

  const addItem = useCallback(() => setItems((prev) => [...prev, newItem()]), []);
  const updateItem = useCallback((id: string, patch: Partial<ContentItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.length <= 1 ? [newItem()] : prev.filter((i) => i.id !== id));
  }, []);

  const exportText = useCallback(() => {
    const lines = [
      "DRR — Contents Inventory",
      `Total items: ${items.length}`,
      "",
      ...items.map((i) => `${i.description || "(no description)"} | Qty: ${i.quantity} | Value: $${i.replacementValue.toLocaleString()} | Status: ${STATUS_LABEL[i.status]}`),
      "",
      `Total Loss: ${contentsTotals.totalLossCount} items — $${contentsTotals.totalLossValue.toLocaleString()}`,
      `Salvageable: ${contentsTotals.salvageableCount} items — $${contentsTotals.salvageableValue.toLocaleString()}`,
      `Cleanable: ${contentsTotals.cleanableCount} items — $${contentsTotals.cleanableValue.toLocaleString()}`,
    ];
    navigator.clipboard.writeText(lines.join("\n")).catch((e) => console.warn('[fire-smoke] clipboard error:', e instanceof Error ? e.message : e));
  }, [items, contentsTotals]);

  const panelStyle = { background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "20px 22px", marginBottom: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.55)" };
  const sectionTitle = { fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#D4A84B", marginBottom: 14 };

  const fmtUsd = (n: number) => `$${n.toLocaleString("en-US")}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* Structure volume — the fallback that lets this tab work before any
          room exists. Same shape as the Mold and Water panels. */}
      <div style={panelStyle}>
        <p style={sectionTitle}>Structure Volume</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.70)", marginBottom: 12 }}>
          {manualVolume > 0
            ? `Using the dimensions below: ${fmt(volume)} ft³. Clear them to go back to your room data.`
            : volume > 0
              ? `Using room summary: ${fmt(volume)} ft³. Enter dimensions below to override.`
              : "No rooms added — enter the affected dimensions to size treatment and scrubbers, or add rooms in Job Setup above."}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <NumField id="fs-l" label="Length (ft)" value={manualL} onChange={setManualL} placeholder="auto" />
          <NumField id="fs-w" label="Width (ft)" value={manualW} onChange={setManualW} placeholder="auto" />
          <NumField id="fs-h" label="Ceiling height (ft)" value={manualH} onChange={setManualH} placeholder="auto" />
        </div>
        <div style={{ marginTop: 12 }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Volume</span>
          <p style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{volume ? fmt(volume) : "—"} ft³</p>
        </div>
      </div>

      {/* Section 1 — Ozone vs Hydroxyl */}
      <div style={panelStyle}>
        <p style={sectionTitle}>1 — Ozone vs. Hydroxyl Treatment</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {([
            { value: "occupied", label: "Occupied / Pets Present", desc: "Use Hydroxyl — safe, slower" },
            { value: "vacatable", label: "Can Vacate 24–48 h", desc: "Ozone — faster, more effective" },
          ] as const).map((opt) => (
            <button key={opt.value} type="button" onClick={() => setOccupancy(opt.value)}
              style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid " + (occupancy === opt.value ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: occupancy === opt.value ? "rgba(212,168,75,0.15)" : "transparent", cursor: "pointer", textAlign: "left" }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: occupancy === opt.value ? "#D4A84B" : "#ffffff" }}>{opt.label}</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.60)", marginTop: 3 }}>{opt.desc}</p>
            </button>
          ))}
        </div>

        <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px", marginBottom: 14 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: treatmentRec.useHydroxyl ? "#4ade80" : "#f87171", marginBottom: 10 }}>
            Recommendation: {treatmentRec.useHydroxyl ? "Hydroxyl Generation" : "Ozone Treatment"}
          </p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.65, marginBottom: 10 }}>
            {treatmentRec.useHydroxyl
              ? "Hydroxyl generators are safe for occupied spaces and do not require evacuation. They work slower (typically 3–5× longer treatment time) but pose no risk to people, pets, or plants. Coverage: 1 unit per 400–600 ft³."
              : "Ozone is faster and more effective for heavy smoke, but requires complete evacuation of people, pets, and plants for 24–48 hours. All HVAC intakes must be sealed. Coverage: 1 unit per 500–800 ft³."}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Units (low)</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{treatmentRec.unitCountLow || "—"}</p>
            </div>
            <div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Units (high)</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#D4A84B", fontVariantNumeric: "tabular-nums" }}>{treatmentRec.unitCountHigh || "—"}</p>
            </div>
            <div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Turnover time</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{treatmentRec.cycleMinutes ? `${fmt(treatmentRec.cycleMinutes, 1)} min` : "—"}</p>
            </div>
          </div>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 10 }}>Coverage: {treatmentRec.coverageMin}–{treatmentRec.coverageMax} ft³ per unit · Volume: {fmt(volume)} ft³</p>
        </div>

        <NumField id="fs-coverage" label="Unit coverage (ft³/unit) — override" value={unitCoverage} onChange={setUnitCoverage} placeholder="600" />
      </div>

      {/* Section 2 — Air Scrubber */}
      <div style={panelStyle}>
        <p style={sectionTitle}>2 — Air Scrubber Sizing</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {([
            { value: "heavy", label: "Heavy Smoke", desc: "Filter every 12–24 h" },
            { value: "moderate", label: "Moderate Smoke", desc: "Filter every 48 h" },
          ] as const).map((opt) => (
            <button key={opt.value} type="button" onClick={() => setSmokeLevel(opt.value)}
              style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid " + (smokeLevel === opt.value ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: smokeLevel === opt.value ? "rgba(212,168,75,0.15)" : "transparent", cursor: "pointer", textAlign: "left" }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: smokeLevel === opt.value ? "#D4A84B" : "#ffffff" }}>{opt.label}</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.60)", marginTop: 3 }}>{opt.desc}</p>
            </button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2" style={{ marginBottom: 14 }}>
          <NumField id="fs-scrubber-cfm" label="Scrubber CFM (per unit)" value={scrubberCfm} onChange={setScrubberCfm} placeholder="500" />
          <div className="space-y-1.5">
            <Label htmlFor="fs-ach-scrub" style={{ color: "rgba(255,255,255,0.85)" }}>Target ACH</Label>
            <select id="fs-ach-scrub" value={targetAchScrub} onChange={(e) => setTargetAchScrub(e.target.value)}
              style={{ width: "100%", height: 40, borderRadius: 8, border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.09)", color: "#ffffff", padding: "0 10px", fontSize: 13 }}>
              <option value="4">4 ACH — baseline</option>
              <option value="6">6 ACH — heavy smoke</option>
              <option value="8">8 ACH — aggressive</option>
            </select>
          </div>
        </div>

        <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Scrubbers</p>
              <p style={{ fontSize: 32, fontWeight: 700, color: "#D4A84B", fontVariantNumeric: "tabular-nums" }}>{scrubbers.count || "—"}</p>
            </div>
            <div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Required CFM</p>
              <p style={{ fontSize: 32, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{scrubbers.reqCfm ? fmt(scrubbers.reqCfm) : "—"}</p>
            </div>
            <div>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Filter interval</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{scrubbers.filterIntervalHours} h</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>charcoal filter</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3 — Contents Inventory */}
      <div style={panelStyle}>
        <p style={sectionTitle}>3 — Contents Inventory</p>

        <div style={{ overflowX: "auto", marginBottom: 14 }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 6px", minWidth: 600 }}>
            <thead>
              <tr style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.6)" }}>
                <th style={{ textAlign: "left", padding: "0 8px 4px" }}>Description</th>
                <th style={{ textAlign: "left", padding: "0 8px 4px", width: 70 }}>Qty</th>
                <th style={{ textAlign: "left", padding: "0 8px 4px", width: 120 }}>Replace $ (ea)</th>
                <th style={{ textAlign: "left", padding: "0 8px 4px", width: 130 }}>Status</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ padding: "3px 6px" }}>
                    <input value={item.description} onChange={(e) => updateItem(item.id, { description: e.target.value })} placeholder="Item description" style={{ width: "100%", height: 36, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, color: "#ffffff", padding: "0 10px", fontSize: 13 }} />
                  </td>
                  <td style={{ padding: "3px 6px" }}>
                    <input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(item.id, { quantity: Math.max(1, parseInt(e.target.value) || 1) })} style={{ width: "100%", height: 36, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, color: "#ffffff", padding: "0 8px", fontSize: 13, textAlign: "center" }} />
                  </td>
                  <td style={{ padding: "3px 6px" }}>
                    <input type="number" min={0} value={item.replacementValue} onChange={(e) => updateItem(item.id, { replacementValue: Math.max(0, parseFloat(e.target.value) || 0) })} placeholder="0" style={{ width: "100%", height: 36, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, color: "#ffffff", padding: "0 8px", fontSize: 13 }} />
                  </td>
                  <td style={{ padding: "3px 6px" }}>
                    <select value={item.status} onChange={(e) => updateItem(item.id, { status: e.target.value as ItemStatus })} style={{ width: "100%", height: 36, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, color: STATUS_COLOR[item.status], padding: "0 8px", fontSize: 12, fontWeight: 700 }}>
                      <option value="total-loss">Total Loss</option>
                      <option value="salvageable">Salvageable</option>
                      <option value="cleanable">Cleanable</option>
                    </select>
                  </td>
                  <td style={{ padding: "3px 6px" }}>
                    <button type="button" onClick={() => removeItem(item.id)} style={{ height: 36, width: 36, background: "transparent", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 16 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <button type="button" onClick={addItem} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(212,168,75,0.5)", background: "transparent", color: "#D4A84B", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Add Item</button>
          <button type="button" onClick={exportText} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.20)", background: "transparent", color: "rgba(255,255,255,0.75)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Copy as Text</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {[
            { label: "Total Loss", count: contentsTotals.totalLossCount, value: contentsTotals.totalLossValue, color: "#f87171" },
            { label: "Salvageable", count: contentsTotals.salvageableCount, value: contentsTotals.salvageableValue, color: "#4ade80" },
            { label: "Cleanable", count: contentsTotals.cleanableCount, value: contentsTotals.cleanableValue, color: "#D4A84B" },
          ].map((cat) => (
            <div key={cat.label} style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${cat.color}30`, borderRadius: 10, padding: "14px 16px" }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: cat.color, textTransform: "uppercase", letterSpacing: "0.08em" }}>{cat.label}</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums", margin: "6px 0 2px" }}>{cat.count} items</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: cat.color, fontVariantNumeric: "tabular-nums" }}>{fmtUsd(cat.value)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
